import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import "../css/Login.css";
import VelaKoda from "../assets/Images/LoginLogo.svg";
import VelaKodaMobile from "../assets/Images/MobileLogo.svg";
import useEnterpriseLogo from "../hooks/useEnterpriseLogo";
import { showErrorToast, showSuccessToast } from "../utils/toastUtils";

const images = [
  "/img/superadminbg2.png",
  "/img/superadminbg5.png",
  "/img/superadminbg3.png",
  "/img/superadminbg4.png",
];

const CompleteRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { logoSrc: companyLogoSrc } = useEnterpriseLogo({
    defaultLogoSrc: VelaKodaMobile,
  });
  const [currentImage, setCurrentImage] = useState(0);
  const [registration, setRegistration] = useState(null);
  const [pageState, setPageState] = useState("validating");
  const [pageMessage, setPageMessage] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const interval = setInterval(
      () => setCurrentImage((value) => (value + 1) % images.length),
      2000,
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let active = true;
    const validateToken = async () => {
      if (!token) {
        setPageState("invalid");
        setPageMessage("This registration link is missing its token.");
        return;
      }
      try {
        const response = await api.get(
          "/auth/complete-registration/validate-token",
          { params: { token }, silent: true, skipAuth: true },
        );
        if (active) {
          setRegistration(response.data.registration);
          setPageState("ready");
        }
      } catch (error) {
        if (active) {
          setPageState("invalid");
          setPageMessage(
            error.response?.data?.message ||
              "This registration link is invalid or has expired.",
          );
        }
      }
    };
    validateToken();
    return () => {
      active = false;
    };
  }, [token]);

  const validateForm = () => {
    const nextErrors = {};
    if (!/^[A-Za-z0-9._-]{3,50}$/.test(username.trim())) {
      nextErrors.username =
        "Use 3-50 letters, numbers, dots, underscores, or hyphens.";
    }
    if (
      password.length < 8 ||
      !/[a-z]/.test(password) ||
      !/[A-Z]/.test(password) ||
      !/\d/.test(password) ||
      !/[^A-Za-z0-9]/.test(password)
    ) {
      nextErrors.password =
        "Use 8+ characters with upper, lower, number, and special character.";
    }
    if (password !== confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading || !validateForm()) return;
    setLoading(true);
    try {
      const response = await api.post(
        "/auth/complete-registration",
        {
          token,
          username: username.trim(),
          password,
          confirmPassword,
        },
        { silent: true, skipAuth: true },
      );
      const {
        access_token,
        refresh_token,
        session_id,
        role_name,
        user,
        redirect_to,
      } = response.data;
      const storedUser = { ...user, role_name, must_reset_password: false };
      [localStorage, sessionStorage].forEach((storage) => {
        storage.setItem("access_token", access_token);
        storage.setItem("refresh_token", refresh_token);
        storage.setItem("session_id", session_id);
        storage.setItem("role_name", role_name);
        storage.setItem("user", JSON.stringify(storedUser));
      });
      showSuccessToast("Registration completed. Welcome to VelaKoda!");
      navigate(redirect_to || "/user/new", { replace: true });
    } catch (error) {
      const responseErrors = error.response?.data?.errors;
      if (responseErrors) setErrors(responseErrors);
      showErrorToast(
        error.response?.data?.message || "Unable to complete registration.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        {images.map((image, index) => (
          <div
            key={image}
            className={`bg-slide ${index === currentImage ? "active" : ""}`}
            style={{ backgroundImage: `url(${image})` }}
          >
            <img src={VelaKoda} alt="VelaKoda" className="slide-logo" />
          </div>
        ))}
      </div>
      <img src={VelaKodaMobile} alt="VelaKoda" className="mobile-logo profile-i" />
      <div className="login-right d-flex flex-column align-items-start justify-content-center">
        <div className="logo-wrapper">
          <img className="zerobox-logo" src={companyLogoSrc} alt="VelaKoda" />
        </div>
        <div className="login-form p-4 rounded-3 mx-auto" style={{ maxWidth: "420px", width: "100%" }}>
          {pageState === "validating" && (
            <div className="text-center py-5">
              <div className="spinner-border" role="status" aria-label="Validating registration link" />
              <p className="login-p mt-3">Validating your registration link...</p>
            </div>
          )}
          {pageState === "invalid" && (
            <div className="text-center py-4">
              <h3 className="mb-3 refresh-prompts fw-bolder">Link unavailable</h3>
              <p className="login-p">{pageMessage}</p>
              <Link to="/" className="custom-btn d-inline-block text-decoration-none mt-2">Return to Login</Link>
            </div>
          )}
          {pageState === "ready" && (
            <form onSubmit={handleSubmit} noValidate>
              <h3 className="mb-3 mt-2 refresh-prompts text-center fw-bolder">Complete Registration</h3>
              <p className="text-center login-p mx-2 refresh-prompts">
                Welcome, {registration?.lastName}. Create your credentials to access your workspace.
              </p>
              <div className="w-100 mb-3">
                <div className="rounded input-group custom-search-group">
                  <span className="input-group-text"><i className="bi bi-person-badge-fill" /></span>
                  <input className={`form-control ${errors.username ? "is-invalid" : ""}`} value={username} onChange={(event) => setUsername(event.target.value)} placeholder="Username" autoComplete="username" maxLength={50} autoFocus />
                </div>
                {errors.username && <div className="text-danger small mt-1">{errors.username}</div>}
              </div>
              <div className="w-100 mb-3">
                <div className="rounded input-group custom-search-group">
                  <span className="input-group-text"><i className="bi bi-lock-fill" /></span>
                  <input type={showPassword ? "text" : "password"} className={`form-control ${errors.password ? "is-invalid" : ""}`} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Password" autoComplete="new-password" />
                  <button type="button" className="input-group-text border-0" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}>
                    <i className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`} />
                  </button>
                </div>
                {errors.password && <div className="text-danger small mt-1">{errors.password}</div>}
              </div>
              <div className="w-100 mb-3">
                <div className="rounded input-group custom-search-group">
                  <span className="input-group-text"><i className="bi bi-shield-lock-fill" /></span>
                  <input type={showPassword ? "text" : "password"} className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Confirm Password" autoComplete="new-password" />
                </div>
                {errors.confirmPassword && <div className="text-danger small mt-1">{errors.confirmPassword}</div>}
              </div>
              <button type="submit" className="custom-btn w-100" disabled={loading}>
                {loading ? "Completing Registration..." : "Complete Registration"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompleteRegistration;
